// Ported verbatim (logic-for-logic) from the prototype's js/player-utils.js —
// the "Keep" file in Ben's inventory ("pure functions … becomes a named ES
// module import"). Typed here for the v3 hooks/components. The legacy classic
// script (public/js/player-utils.js, window.getPlayerShortName etc.) was deleted in
// 05k-2 — this is the only copy.

import { familiarNameOf } from "./familiar-names.js";

interface NamedPlayer {
  name?: string | null;
}

// A surname is more than its last word (Ben, 2026-10-31: "Micky van de Ven would never appear as
// Micky Ven"). The small words before it belong to it — Dutch "van" / "van de" / "ter", German
// "von", Romance "de" / "da" / "dos" / "del" / "di" / "la", Arabic "El" / "Al" / "Ben" / "Bin" —
// in any case ("De Bruyne", "El Neny", "Ben Yedder"). A last word like "Júnior" or "Neto" goes
// with the word before it ("Vinícius Júnior" style). The first name is never taken.
const PARTICLES: ReadonlySet<string> = new Set([
  "van", "von", "vom", "der", "den", "de", "del", "della", "dei", "degli", "di", "da", "das", "do",
  "dos", "du", "la", "le", "lo", "ter", "ten", "te", "zu", "zum", "zur", "af", "av", "al", "el",
  "ben", "bin", "ibn", "abu", "st", "st.", "mac", "mc",
]);
const SUFFIXES: ReadonlySet<string> = new Set(["júnior", "junior", "jr", "jr.", "neto", "filho", "sobrinho", "ii", "iii"]);

/** Where the surname starts in a name's words (≥ 1: never the first name). */
function surnameStart(parts: readonly string[]): number {
  let i = parts.length - 1;
  if (i > 1 && SUFFIXES.has(parts[i]!.toLowerCase())) i--;
  while (i > 1 && PARTICLES.has(parts[i - 1]!.toLowerCase())) i--;
  return Math.max(i, 1);
}

/** The surname, its particles and suffix kept ("van de Ven"); a one-word name is its own. */
function surnameOf(parts: readonly string[]): string {
  return parts.length === 1 ? (parts[0] ?? "") : parts.slice(surnameStart(parts)).join(" ");
}

// Catalyst's own `nickname` field is inconsistent (e.g. Alessia Mia Teresa
// Russo's is "Alessia Teresa", dropping her real surname), so the demo's display
// name is derived mechanically: first + last, dropping anything in between.
// Two-word names pass through unchanged. ?live=1: the familiar name first ("Lionel
// Messi"), with our corrections for the nicknames that are wrong (Ben, 2026-10-17).
export function getPlayerShortName(player: NamedPlayer | null | undefined): string {
  const full = (player?.name ?? "").trim();
  if (!full) return "";
  const known = familiarNameOf(full);
  if (known) return known;
  const parts = full.split(/\s+/);
  if (parts.length <= 2) return full;
  return (parts[0] ?? "") + " " + surnameOf(parts);
}

// Compact "A.Surname" form — used where two players' names sit side by side
// (comparison legend chips) and a full first name is just noise.
export function getPlayerInitialName(player: NamedPlayer | null | undefined): string {
  const legal = (player?.name ?? "").trim();
  if (!legal) return "";
  // A familiar name keeps all its surname words ("K.De Bruyne"); one word stays whole ("Rodri").
  const known = familiarNameOf(legal);
  if (known) {
    const [first = "", ...rest] = known.split(/\s+/);
    return rest.length ? `${first.charAt(0)}.${rest.join(" ")}` : known;
  }
  const full = legal;
  const parts = full.split(/\s+/);
  if (parts.length === 1) return full;
  return (parts[0] ?? "").charAt(0) + "." + surnameOf(parts);
}

// Surname only — used in Radar overlay tooltips where the name sits next to a
// season code and a first name would just push the number away.
export function getPlayerLastName(player: NamedPlayer | null | undefined): string {
  const full = (player?.name ?? "").trim();
  if (!full) return "";
  // A familiar name: everything after the first name ("De Bruyne"); one word stays whole ("Rodri").
  const known = familiarNameOf(full);
  if (known) {
    const rest = known.split(/\s+/).slice(1);
    return rest.length ? rest.join(" ") : known;
  }
  return surnameOf(full.split(/\s+/));
}

/** The surname as a list files it: its small words left off ("van de Ven" under V for Ven,
 *  "van Domselaar" under D), as Dutch and English lists do. */
function sortSurname(p: NamedPlayer): string {
  const words = getPlayerLastName(p).split(/\s+/);
  while (words.length > 1 && PARTICLES.has(words[0]!.toLowerCase())) words.shift();
  return words.join(" ");
}

// Standing app-wide rule: anywhere players are listed alphabetically, order by
// surname (a scout thinks "Russo" before "Alessia"). Ties fall back to the full
// short name for a stable secondary order.
export function comparePlayersByName(a: NamedPlayer, b: NamedPlayer): number {
  const cmp = sortSurname(a).localeCompare(sortSurname(b));
  if (cmp !== 0) return cmp;
  return getPlayerShortName(a).localeCompare(getPlayerShortName(b));
}
